import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useVaultLinks } from '@shared/lib/vault-links';
import { useToastStore } from '@shared/stores/toast.store';

import { useChatConversationId } from '../components/chat-context';
import {
  checkVaultFile,
  checkVaultFiles,
  trackVaultFile,
  useVaultAvailabilityStore,
  type VaultFileStatus,
} from '../data/vault-availability';

export interface VaultFileLink {
  vaultFileId: string;
  status: VaultFileStatus;
  removed: boolean;
  canDownload: boolean;
  removedOnOpen: boolean;
  open: (options?: { beforeNavigate?: () => void }) => Promise<boolean>;
  download: () => void;
}

export function useVaultFileStatus(vaultFileId: string | undefined): VaultFileStatus | undefined {
  return useVaultAvailabilityStore((state) =>
    vaultFileId === undefined ? undefined : state.statuses[vaultFileId],
  );
}

const ID_SEPARATOR = '\n';

export function useTrackVaultFiles(vaultFileIds: readonly (string | undefined)[]): void {
  const links = useVaultLinks();
  const key = [
    ...new Set(vaultFileIds.filter((id): id is string => id !== undefined && id !== '')),
  ].join(ID_SEPARATOR);

  useEffect(() => {
    if (!links || key === '') return;
    const ids = key.split(ID_SEPARATOR);
    const releases = ids.map((id) => trackVaultFile(id));
    checkVaultFiles(ids);
    return () => {
      for (const release of releases) release();
    };
  }, [links, key]);
}

export function useVaultFileLink(vaultFileId: string | undefined): VaultFileLink | null {
  const links = useVaultLinks();
  const conversationId = useChatConversationId();
  const { t } = useTranslation('ai-cio');
  const storedStatus = useVaultFileStatus(vaultFileId);
  const [removedOnOpenId, setRemovedOnOpenId] = useState<string>();
  const fileId = vaultFileId === '' ? undefined : vaultFileId;
  const active = fileId !== undefined && links !== null;

  useEffect(() => {
    if (!active) return;
    const release = trackVaultFile(fileId);
    void checkVaultFile(fileId);
    return release;
  }, [active, fileId]);

  return useMemo(() => {
    if (fileId === undefined || links === null) return null;
    const status = storedStatus ?? 'unknown';
    const removed = status === 'removed';
    return {
      vaultFileId: fileId,
      status,
      removed,
      canDownload: status === 'available' || status === 'unknown',
      removedOnOpen: removed && removedOnOpenId === fileId,
      open: async (options) => {
        const checked = await checkVaultFile(fileId, { force: true });
        if (checked === 'removed') {
          setRemovedOnOpenId(fileId);
          return false;
        }
        options?.beforeNavigate?.();
        links.open(fileId, { conversationId });
        return true;
      },
      download: () => {
        links.download(fileId).catch(() => {
          useToastStore.getState().addToast({
            variant: 'error',
            title: t('vaultLink.downloadFailed', 'Couldn’t download the file. Try again.'),
          });
        });
      },
    };
  }, [fileId, links, storedStatus, removedOnOpenId, conversationId, t]);
}
